/**
 * Auth Context - PRODUCTION Firebase Auth
 * Single auth provider for the entire application
 */
import { useState, useEffect, createContext, useContext, useCallback, useMemo, useRef } from "react";
import { logger } from "../utils/logger";
import { onAuthStateChanged, signOut } from "firebase/auth";
import axios from "axios";
import { toast } from "sonner";
import { auth, isFirebaseConfigured } from "@/firebase/config";
import { checkRedirectResult } from "@/firebase/authProviders";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider - Firebase + Test Login support
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const initRef = useRef(false);

  // === Creator Lock: globalny axios interceptor ===
  // Backend zwraca 403 z headerem `X-PAX-Locked: true` gdy user nie-admin
  // próbuje request'u przy włączonym Creator Lock. Zamiast pokazać toast
  // z błędem, redirect'ujemy na `/locked` (pełny hard reload żeby wyczyścić
  // React Query cache).
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        try {
          if (
            error?.response?.status === 403 &&
            (error?.response?.headers?.["x-pax-locked"] === "true" ||
              error?.response?.data?.creator_lock === true)
          ) {
            if (window.location.pathname !== "/locked") {
              window.location.href = "/locked";
            }
          }
        } catch (_) {
          // best-effort
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  /**
   * Get current Firebase ID token for API calls
   */
  const getIdToken = useCallback(async () => {
    if (firebaseUser) {
      try {
        return await firebaseUser.getIdToken(true);
      } catch (error) {
        logger.error('[Auth] Failed to get ID token:', error);
        return null;
      }
    }
    return null;
  }, [firebaseUser]);

  /**
   * Check session cookie (for test login or Firebase sessions)
   */
  const checkSessionCookie = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      if (response.data) {
        setUser(response.data);
        return response.data;
      }
    } catch (error) {
      // No valid session
      logger.log('[Auth] No session cookie');
    }
    return null;
  }, []);

  /**
   * Sync Firebase user with backend
   */
  const syncFirebaseUser = useCallback(async (fbUser) => {
    if (!fbUser) {
      setUser(null);
      return null;
    }

    try {
      const idToken = await fbUser.getIdToken(true);
      
      const response = await axios.post(
        `${API}/auth/firebase/sync`,
        {
          email: fbUser.email,
          name: fbUser.displayName || fbUser.email?.split('@')[0],
          picture: fbUser.photoURL,
          firebase_uid: fbUser.uid,
          provider: fbUser.providerData?.[0]?.providerId || 'google.com'
        },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );

      setUser(response.data);
      return response.data;
    } catch (error) {
      logger.error('[Auth] Backend sync failed:', error);
      // Fallback user from Firebase data (RBAC usunięte - każdy jest owner)
      const fallbackUser = {
        user_id: fbUser.uid,
        email: fbUser.email,
        name: fbUser.displayName || fbUser.email?.split('@')[0],
        picture: fbUser.photoURL,
        role: 'owner',
        is_active: true
      };
      setUser(fallbackUser);
      return fallbackUser;
    }
  }, []);

  /**
   * Handle Firebase auth state changes
   */
  useEffect(() => {
    // First check for session cookie (test login)
    checkSessionCookie().then(async sessionUser => {
      if (sessionUser) {
        setLoading(false);
        return; // Session found, skip Firebase
      }
      
      // No session, try Firebase
      if (!isFirebaseConfigured()) {
        logger.warn('[Auth] Firebase not configured');
        setLoading(false);
        return;
      }

      // CRITICAL: Wait for redirect result BEFORE attaching onAuthStateChanged.
      // Bez tego, mobile Firebase redirect race-condition z onAuthStateChanged(null)
      // powoduje, że ProtectedRoute przerzuca usera na /login zanim redirect się dohydruje.
      if (!initRef.current) {
        initRef.current = true;
        try {
          const redirectResult = await checkRedirectResult();
          if (redirectResult?.user) {
            logger.log('[Auth] Redirect sign-in successful, syncing...');
            setFirebaseUser(redirectResult.user);
            await syncFirebaseUser(redirectResult.user);
          }
        } catch (error) {
          logger.error('[Auth] Redirect result error:', error);
        }
      }

      const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
        setFirebaseUser(fbUser);
        
        if (fbUser) {
          await syncFirebaseUser(fbUser);
        } else {
          setUser(null);
        }
        
        setLoading(false);
      });

      return () => unsubscribe();
    });
  }, [syncFirebaseUser, checkSessionCookie]);

  /**
   * Logout - kończy sesję aplikacji
   */
  const logout = useCallback(async () => {
    try {
      // 1. Clear backend session
      try {
        const idToken = await getIdToken();
        if (idToken) {
          await axios.post(
            `${API}/auth/logout`,
            {},
            {
              headers: { 'Authorization': `Bearer ${idToken}` },
              withCredentials: true
            }
          );
        } else {
          // Session-based logout (test login)
          await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        }
      } catch (error) {
        logger.warn('[Auth] Backend logout failed:', error);
      }

      // 2. Sign out from Firebase
      if (auth && firebaseUser) {
        await signOut(auth);
      }

      // 3. Clear local state
      setUser(null);
      setFirebaseUser(null);

      // 4. R-SEC-TENANT-005: wyczyść cache prywatnych danych po zmianie
      // konta (żeby nowe konto nie zobaczyło przez chwilę danych z cache
      // poprzedniego). Zachowujemy ustawienia UI (np. locale, sidebar).
      try {
        // localStorage: usuń klucze zawierające dane prywatne (invoices, ksef, contractors, payment, bank).
        const privatePatterns = /^(pax_|invoice|ksef|contractor|payment|bank|agent|vendor|profile|analysis)/i;
        const toRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && privatePatterns.test(key)) toRemove.push(key);
        }
        toRemove.forEach((k) => localStorage.removeItem(k));
        // sessionStorage: wywal całość — to per-session cache.
        sessionStorage.clear();
      } catch (err) {
        logger.warn('[Auth] Cache cleanup failed:', err);
      }

      toast.success('Wylogowano pomyślnie');

      // R-SEC-TENANT-010 (2026-02-17): pełny reload strony po wylogowaniu
      // eliminuje ryzyko wyścigu z opóźnionymi requestami USER_A, które
      // mogłyby zapisać się w state React-a po zalogowaniu USER_B.
      // Aplikacja nie używa React Query, więc `queryClient.clear()` nie ma
      // zastosowania — reload jest najprostszą i pełną gwarancją resetu
      // wszystkich store'ów w pamięci.
      try {
        window.location.href = '/login';
      } catch (_err) {
        /* SSR/testowe środowisko */
      }
    } catch (error) {
      logger.error('[Auth] Logout error:', error);
      // Always clear state even on error
      setUser(null);
      setFirebaseUser(null);
    }
  }, [getIdToken, firebaseUser]);

  /**
   * Refresh user data from backend
   */
  const refreshUser = useCallback(async () => {
    // Try session cookie first (for test login)
    const sessionUser = await checkSessionCookie();
    if (sessionUser) {
      return sessionUser;
    }
    
    // Fall back to Firebase
    if (firebaseUser) {
      return syncFirebaseUser(firebaseUser);
    }
    return null;
  }, [checkSessionCookie, syncFirebaseUser, firebaseUser]);

  // useMemo dla value żeby uniknąć niepotrzebnych re-renderów wszystkich konsumentów useAuth()
  const contextValue = useMemo(() => ({
    user,
    loading,
    logout,
    refreshUser,
    getIdToken,
    isAuthenticated: !!user
  }), [user, loading, logout, refreshUser, getIdToken]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
